import { cn } from '@doccareplus/shared/lib/utils';

interface AvatarProps {
  /** Display name — initials are derived automatically (first two words) */
  name: string;
  /** Override computed initials */
  initials?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Use the brand gradient background (for clinic/org avatars) */
  brand?: boolean;
  /** Optional status dot colour (CSS colour string) */
  statusColor?: string;
  className?: string;
}

const SIZE_MAP = {
  xs: { outer: 'w-[24px] h-[24px] text-[10px]',  dot: 'w-[7px] h-[7px]'  },
  sm: { outer: 'w-[30px] h-[30px] text-[11px]',  dot: 'w-[8px] h-[8px]'  },
  md: { outer: 'w-[32px] h-[32px] text-[11.5px]', dot: 'w-[9px] h-[9px]'  },
  lg: { outer: 'w-[44px] h-[44px] text-[16px]',   dot: 'w-[10px] h-[10px]' },
  xl: { outer: 'w-[54px] h-[54px] text-[20px]',   dot: 'w-[12px] h-[12px]' },
};

/**
 * Avatar — displays user/clinic initials with optional status dot.
 * NO business logic — pure presentational.
 */
export function Avatar({
  name,
  initials,
  size = 'md',
  brand = false,
  statusColor,
  className,
}: AvatarProps) {
  const derived = initials ?? name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  const cfg = SIZE_MAP[size];

  return (
    <span className="relative inline-flex flex-none">
      <span
        className={cn(
          'flex items-center justify-center rounded-full font-bold select-none',
          brand
            ? 'text-white'
            : 'bg-[var(--surface-3)] text-[var(--text)]',
          cfg.outer,
          className
        )}
        style={brand ? { background: 'linear-gradient(135deg,var(--brand-500),var(--brand-700))' } : undefined}
      >
        {derived}
      </span>
      {statusColor && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2 border-[var(--surface)]',
            cfg.dot
          )}
          style={{ background: statusColor }}
        />
      )}
    </span>
  );
}

/* ─── ClinicAvatar (square, branded) ──────────────────────── */
interface ClinicAvatarProps {
  initials: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * ClinicAvatar — square branded avatar for clinic / org selectors.
 */
export function ClinicAvatar({ initials, size = 'md', className }: ClinicAvatarProps) {
  const sizeClass = size === 'md' ? 'w-[34px] h-[34px] text-[13px] rounded-[9px]' : 'w-[28px] h-[28px] text-[11px] rounded-[7px]';
  return (
    <span
      className={cn(
        'flex items-center justify-center font-bold text-white flex-none select-none',
        sizeClass,
        className
      )}
      style={{ background: 'linear-gradient(135deg,var(--brand-500),var(--brand-700))' }}
    >
      {initials}
    </span>
  );
}
